import type { ReactNode } from 'react';
import { toast } from 'react-toastify';
import type { ToastContent } from 'react-toastify';

type PositionTypes =
  | 'top-right'
  | 'top-center'
  | 'top-left'
  | 'bottom-right'
  | 'bottom-center'
  | 'bottom-left';

export const alertMessageSuccessWithLink = (
  message: string,
  linkMessage: string,
  link: string
) => {
  function CustomToastWithLink() {
    return (
      <div>
        <span>{message}</span>
        <button
          type='button'
          onClick={() => {
            window.location.href = link;
          }}
          className='text-blue-600 font-bold'
        >
          {linkMessage}
        </button>
      </div>
    );
  }
  toast.info(CustomToastWithLink, {
    position: 'top-right',
    autoClose: 5000,
    hideProgressBar: false,
    closeOnClick: false,
    pauseOnHover: true,
    draggable: true,
    progress: undefined,
    theme: 'dark',
  });
};

export const alertMessage = (position: PositionTypes, message: string) => {
  toast(message, {
    position,
    autoClose: 4000,
    hideProgressBar: false,
    closeOnClick: true,
    pauseOnHover: true,
    draggable: true,
    progress: undefined,
    theme: 'dark',
  });
};

export const alertMessageSuccess = (message: string) => {
  toast.success(message, {
    position: 'top-right',
    autoClose: 5000,
    hideProgressBar: false,
    closeOnClick: true,
    pauseOnHover: true,
    draggable: true,
    progress: undefined,
    theme: 'dark',
  });
};

/**
 * The same success toast as alertMessageSuccess for a message that carries an element of its own,
 * such as a button inside the toast. A click does not close the toast, so the button gets it.
 */
export const alertMessageSuccessNode = (content: ReactNode) => {
  toast.success(content, {
    position: 'top-right',
    autoClose: 5000,
    hideProgressBar: false,
    closeOnClick: false,
    pauseOnHover: true,
    draggable: true,
    progress: undefined,
    theme: 'dark',
  });
};

/**
 * A warning toast for a message that carries elements of its own, such as the two buttons the Tax
 * Profile page offers when somebody else changed a value first. It stays up longer than a plain
 * message and a click does not close it, so the buttons get the click.
 *
 * `content` may be a function, which react-toastify calls with { closeToast, toastProps }, so a
 * button inside the toast can close it.
 */
export const alertWarningMessageNode = (content: ToastContent) => {
  toast.warning(content, {
    position: 'top-right',
    autoClose: 12000,
    hideProgressBar: false,
    closeOnClick: false,
    pauseOnHover: true,
    draggable: true,
    progress: undefined,
    theme: 'dark',
  });
};

export const alertErrorMessage = (message: string) => {
  toast.error(message, {
    position: 'top-right',
    autoClose: 4000,
    hideProgressBar: false,
    closeOnClick: true,
    pauseOnHover: true,
    draggable: true,
    progress: undefined,
    theme: 'dark',
  });
};

export const alertLoadingMessage = (message: string) => {
  toast.loading(message, {
    position: 'top-right',
    autoClose: 5000,
    hideProgressBar: false,
    closeOnClick: true,
    pauseOnHover: true,
    draggable: true,
    progress: undefined,
    theme: 'dark',
  });
};
